import React, { useState } from 'react';

import { Heading, Container, Text, Button } from 'theme-ui';

import Box from 'elements/Box';
import Spacer from 'elements/Spacer';
import Skeleton from 'components/core/Skeleton';
import OnlineToggle from 'components/OnlineToggle';
import ProfileAvatar from 'components/ProfileAvatar';
import ProfileAvatarOverlay from 'components/ProfileAvatar/ProfileAvatarOverlay';
import ProfileHeader from 'components/ProfileHeader';
import { useGetIdentity } from 'contexts/IdentityProvider';
import { useOnlineStatus } from 'contexts/OnlineStatusProvider';
import {
  useGetFriendsQuery,
  useUpdateUserAvatarMutation,
  Visibility
} from 'generated/schemaTypes';

const Home: React.FC = () => {
  return (
    <Box>
      <Self />
      <Box p={3}>
        <Container
          p={4}
          bg="muted"
          sx={{
            borderRadius: 5,
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Text>Online</Text>
          <OnlineToggle />
        </Container>
        <Spacer />
        <Container p={0}>
          <Heading>Friends</Heading>
          <Spacer size={2} />
          <Friends />
        </Container>
      </Box>
    </Box>
  );
};

const Self: React.FC = () => {
  const [editing, setEditing] = useState(false);
  const { loading, user } = useGetIdentity();
  const { online } = useOnlineStatus();
  const [newAvatar, setNewAvatar] = useState<string | null>(null);
  const [updateMutation] = useUpdateUserAvatarMutation();
  const [fileInfo, setFileInfo] = useState<string | null>(null);

  if (loading) return <span>Loading...</span>;
  if (!user) return <span>Error fetching user</span>;

  const { username, avatar } = user;

  const userAvatar = editing && newAvatar ? newAvatar : avatar;

  const onFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.length) {
      const file = event.target.files[0];
      const urlObj = URL.createObjectURL(file);
      setNewAvatar(urlObj);
      setFileInfo(file.type);
    }
  };

  const onEditClick = () => {
    setEditing(true);
  };

  const onCancelClick = () => {
    setNewAvatar(null);
    setEditing(false);
  };

  const onSaveClick = () => {
    if (newAvatar && fileInfo) {
      const fileType = fileInfo.slice(
        fileInfo.lastIndexOf('/') + 1,
        fileInfo.length
      );
      updateMutation({
        variables: {
          input: {
            file: {
              bucket: 'hubworld-lfg',
              region: 'us-east-1',
              key: `avatars/${username}.${fileType}`,
              localUri: newAvatar,
              mimeType: fileInfo,
              visibility: Visibility.Public
            }
          }
        }
      });
    }
    setNewAvatar(null);
    setEditing(false);
  };

  return (
    <>
      <ProfileHeader />
      <Box
        display="flex"
        flexDirection="row"
        flexWrap="wrap"
        justifyContent="space-between"
        width={1 / 1}
        p={3}
        mb={3}
      >
        <Box display="flex" flexDirection="row" flexWrap="wrap">
          <UserProfileAvatar
            online={online}
            avatar={userAvatar}
            editing={editing}
            onChange={onFileSelect}
          />
          <Spacer />
          <Heading sx={{ wordWrap: 'break-word' }}>{username}</Heading>
        </Box>
      </Box>
    </>
  );
};

const UserProfileAvatar: React.FC<{
  avatar: string;
  online: boolean;
  editing: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}> = ({ avatar, online, editing, onChange }) => {
  return (
    <Box mt={-40} mr={3} position="relative">
      <ProfileAvatar online={online} avatar={avatar} />
      <ProfileAvatarOverlay show={editing} onChange={onChange} />
    </Box>
  );
};
const Friends: React.FC = () => {
  const { loading, data } = useGetFriendsQuery();

  if (loading) return <FriendsSkeleton />;

  if (!data?.me?.friends) return <span>No user / error</span>;

  const friends = data.me.friends.sort((a, b) =>
    a.username.localeCompare(b.username)
  );

  return (
    <Container p={4} bg="muted" sx={{ borderRadius: 5 }}>
      {friends.map(f => (
        <Box
          key={f.id}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Text
            sx={{
              fontSize: 4,
              fontWeight: 'bold'
            }}
          >
            {f.username}
          </Text>
          <ProfileAvatar avatar={f.avatar} online={!!f.online} size={50} />
        </Box>
      ))}
    </Container>
  );
};

const FriendsSkeleton: React.FC = () => (
  <Container p={4} bg="muted" sx={{ borderRadius: 5 }}>
    <Box
      display="grid"
      gridTemplateColumns="1fr 40px"
      gridRowGap={20}
      gridColumnGap={10}
    >
      {[1, 2, 3, 4].map(i => (
        <React.Fragment key={`friends-skele-${i}`}>
          <Skeleton />
          <Skeleton variant="circle" />
        </React.Fragment>
      ))}
    </Box>
  </Container>
);

export default Home;
